// src/scenes/sitemanager/Expenses.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import Header from "../../components/Header";
import MediaViewer from "../../components/mediaviewer";
import LogNotes from "../../components/LogNotes";
import ExpenseEntry from "../../component-files/Forms/MGRExpenseEntry";
import { useAuth } from "../../context/AuthContext";
import { tokens } from "../../theme";

const API_EXPENSES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Expenses";
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_subcategories";
const API_VENDORS = "https://figazuresql.swancreate.com/api/swa/FINANCE_vendors";
const API_REIMBURSEMENT = "https://figazuresql.swancreate.com/api/swa/FINANCE_ReimbursementPrograms";
const API_LOGS = "https://figazuresql.swancreate.com/api/swa/FINANCE_Log";

const Expenses = () => {
    const { allocations } = useAuth();
    const allowedIds = allocations.map(a => a.SchoolId);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // pick row colors based on mode
    const lightWhite = "#ffffff";
    const lightGrey = "#f5f5f5";
    const darkMain = colors.primary[600];
    const darkAlt = colors.primary[500];

    const textColor = theme.palette.text.primary;
    const fileInputRefs = useRef({});

    // data
    const [expenseData, setExpenseData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [subcategoryData, setSubcategoryData] = useState([]);
    const [vendorData, setVendorData] = useState([]);
    const [notesMap, setNotesMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI
    const [isEntryOpen, setIsEntryOpen] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");
    const [logNotesOpen, setLogNotesOpen] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(null);

    // filters
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedSite, setSelectedSite] = useState("");
    const [selectedSubcategory, setSelectedSubcategory] = useState("");
    const [selectedVendor, setSelectedVendor] = useState("");
    const [selectedInvoice, setSelectedInvoice] = useState("");

        useEffect(() => {
               Promise.all([
                       axios.get(API_CATEGORIES),
                      axios.get(API_SUBCATEGORIES),
                        axios.get(API_VENDORS),
                        axios.get(API_LOGS),
                    ])
                       .then(([c, sc, v, l]) => {
                                setCategoryData(c.data);
                               setSubcategoryData(sc.data);
                               setVendorData(v.data);
                               const m = {};
                               l.data.forEach(log => {
                                       m[log.eventid] =[...(m[log.eventid] || []), log];
                                    });
                                setNotesMap(m);
                           })
                        .catch(console.error)
                        .finally(fetchExpenses);
           }, [allocations]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(API_EXPENSES);
            setExpenseData(data.filter(e => allowedIds.includes(e.SchoolId)));
        } catch {
            setError("Failed to load expenses");
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (apiid, file) => {
        // your existing media upload → PUT back to expense
    };

    const handleDelete = async apiid => {
        try {
            await axios.delete(`${API_EXPENSES}/${apiid}`);
            setExpenseData(prev => prev.filter(e => e.apiid !== apiid));
        } catch {
            setError("Delete failed");
        }
    };

    const handleAddNote = eventid => {
        setSelectedEventId(eventid);
        setLogNotesOpen(true);
    };

    const months = Array.from(
        new Set(
            expenseData
                .filter(e => e.ExpenseDate)
                .map(e =>
                    new Date(e.ExpenseDate).toLocaleString("en-US", { month: "long" })
                )
        )
    );

    const filtered = expenseData.filter(e => {
        const m = !selectedMonth ||
            new Date(e.ExpenseDate).toLocaleString("en-US", { month: "long" }) ===
            selectedMonth;
        const s = !selectedSite || e.SiteName === selectedSite;
        const sc = !selectedSubcategory || e.ExpenseSubcategory === selectedSubcategory;
        const v = !selectedVendor || e.VendorName === selectedVendor;
        const i = !selectedInvoice ||
            (e.InvoiceNumber || "")
                .toLowerCase()
                .includes(selectedInvoice.toLowerCase());
        return m && s && sc && v && i;
    });

    const formatDate = d =>
        d
            ? new Date(d).toLocaleDateString("en-US", {
                year: "numeric", month: "2-digit", day: "2-digit"
            })
            : "";

    const columns = [
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: p => {
                const id = p.row.apiid;
                return (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Delete"><IconButton onClick={() => handleDelete(id)}><DeleteIcon color="error" /></IconButton></Tooltip>
                        <Tooltip title="Invoice Details"><IconButton><InfoOutlinedIcon color="info" /></IconButton></Tooltip>
                        {p.row.imageURL ? (
                            <Tooltip title="View Receipt">
                                <IconButton onClick={() => { setSelectedImage(p.row.imageURL); setImageModalOpen(true); }}>
                                    <ImageOutlinedIcon sx={{ color: "blue" }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <>
                                <input
                                    type="file"
                                    style={{ display: "none" }}
                                    ref={el => (fileInputRefs.current[id] = el)}
                                    onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(id, f); }}
                                />
                                <Tooltip title="Upload Receipt">
                                    <IconButton onClick={() => fileInputRefs.current[id]?.click()}>
                                        <ImageOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip title="Add Note">
                            <IconButton onClick={() => handleAddNote(p.row.ExpenseID)}>
                                <NoteAddIcon color="secondary" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
        { field: "InvoiceNumber", headerName: "Invoice #", flex: 1 },
        {
            field: "SiteName",
            headerName: "Site",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: allocations.map(a => a.SchoolName),
            valueSetter: p => {
                const o = allocations.find(a => a.SchoolName === p.value);
                if (o) { p.row.SiteName = o.SchoolName; p.row.SchoolId = o.SchoolId; }
                return { ...p.row };
            },
        },
        {
            field: "VendorName",
            headerName: "Vendor",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: vendorData.map(v => v.VendorName),
            valueSetter: p => {
                const v = vendorData.find(x => x.VendorName === p.value);
                if (v) { p.row.VendorName = v.VendorName; p.row.VendorID = v.VendorID; }
                return { ...p.row };
            },
        },
        {
            field: "ExpenseCategory",
            headerName: "Category",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: categoryData.map(c => c.CategoryName),
            valueSetter: p => {
                const c = categoryData.find(x => x.CategoryName === p.value);
                if (c) { p.row.ExpenseCategory = c.CategoryName; p.row.ExpenseCategoryID = c.CategoryID; }
                return { ...p.row };
            },
        },
        {
            field: "ExpenseSubcategory",
            headerName: "Subcategory",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: p =>
                subcategoryData
                    .filter(s => s.CategoryID === p.row.ExpenseCategoryID)
                    .map(s => s.SubcategoryName),
            valueSetter: p => {
                const s = subcategoryData.find(x => x.SubcategoryName === p.value);
                if (s) { p.row.ExpenseSubcategory = s.SubcategoryName; p.row.ExpenseSubCategoryID = s.SubcategoryID; }
                return { ...p.row };
            },
        },
        {
            field: "Amount",
            headerName: "Amount",
            flex: 1,
            editable: true,
            valueFormatter: p =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(p.value || 0),
        },
        {
            field: "ExpenseDate",
            headerName: "Date",
            flex: 1,
            editable: true,
            valueFormatter: p => formatDate(p.value),
        },
        { field: "Description", headerName: "Description", flex: 2 },
    ];

    return (
        <Box
            m={2}
            sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                p: 2,
            }}
        >
            <Header title="EXPENSES" subtitle="Enter expenses for your sites" />

            {/* filters */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                {[
                    { label: "Month", value: selectedMonth, onChange: setSelectedMonth, options: months },
                    { label: "Site", value: selectedSite, onChange: setSelectedSite, options: allocations.map(a => a.SchoolName) },
                    { label: "Vendor", value: selectedVendor, onChange: setSelectedVendor, options: vendorData.map(v => v.VendorName) },
                    { label: "Subcategory", value: selectedSubcategory, onChange: setSelectedSubcategory, options: subcategoryData.map(s => s.SubcategoryName) },
                ].map(({ label, value, onChange, options }) => (
                    <FormControl
                        size="small"
                        key={label}
                        sx={{
                            minWidth: 180,
                            "& .MuiInputLabel-root, .MuiSelect-icon": { color: textColor },
                        }}
                    >
                        <InputLabel>{label}</InputLabel>
                        <Select
                            value={value}
                            label={label}
                            onChange={e => onChange(e.target.value)}
                            sx={{ color: textColor }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {options.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ))}

                <TextField
                    size="small"
                    label="Invoice #"
                    value={selectedInvoice}
                    onChange={e => setSelectedInvoice(e.target.value)}
                    sx={{
                        minWidth: 180,
                        "& .MuiInputLabel-root, .MuiOutlinedInput-input": { color: textColor },
                    }}
                />

                <Button variant="contained" onClick={() => setIsEntryOpen(true)}>
                    Add Expense
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Box height="70vh">
                    <DataGrid
                        rows={filtered}
                        columns={columns}
                        getRowId={r => r.apiid}
                        pageSize={25}
                        rowsPerPageOptions={[25, 50]}
                        disableSelectionOnClick
                        density="compact"
                        getRowClassName={params =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                        }
                        sx={{
                            "& .even": { backgroundColor: theme.palette.mode === "light" ? lightGrey : darkAlt },
                            "& .odd": { backgroundColor: theme.palette.mode === "light" ? lightWhite : darkMain },
                            "& .MuiDataGrid-cell": { color: textColor },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: colors.primary[700],
                                color: textColor,
                            },
                            "& .MuiDataGrid-footerContainer": {
                                backgroundColor: colors.primary[700],
                                color: textColor,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Entry modal */}
            <Modal open={isEntryOpen} onClose={() => setIsEntryOpen(false)}>
                <Box
                    sx={{
                        width: "80%",
                        p: 2,
                        mx: "auto",
                        mt: "5%",
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <ExpenseEntry
                        onClose={() => setIsEntryOpen(false)}
                        onSaved={() => {
                            setIsEntryOpen(false);
                            fetchExpenses();
                        }}
                    />
                </Box>
            </Modal>

            {/* Image viewer */}
            <MediaViewer
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                src={selectedImage}
                type="image"
            />

            {/* Log notes */}
            <Modal open={logNotesOpen} onClose={() => setLogNotesOpen(false)}>
                <Box
                    sx={{
                        width: 400,
                        p: 2,
                        mx: "auto",
                        mt: "10%",
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <LogNotes eventId={selectedEventId} />
                    <Button fullWidth onClick={() => setLogNotesOpen(false)}>
                        Close
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
};

export default Expenses;
